<template>
  <v-container fluid>
    <v-card class="elevation-3 dashboard-card">
      <v-card-title class="primary white--text">
        <v-icon class="mr-2" color="white">fa-chart-line</v-icon>
        {{ labels.reportTitle }}
      </v-card-title>
      
      <v-card-text>
        <!-- Bộ lọc -->
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  :label="labels.fromDate"
                  prepend-icon="fa-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                @input="fromDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  :label="labels.toDate"
                  prepend-icon="fa-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                @input="toDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3" v-if="!selectedCustomerID">
            <v-select
              v-model="selectedCustomer"
              :items="customerOptions"
              item-text="text"
              item-value="value"
              :label="labels.agent"
              prepend-icon="fa-account"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" md="3" class="pt-6">
            <v-btn color="primary" @click="loadStatistics" :loading="loading">
              <v-icon left>fa-refresh</v-icon>
              {{ labels.update }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Tổng quan thống kê -->
        <v-row class="mt-4" v-if="totalSummary">
          <v-col cols="12" sm="6" md="3" v-for="card in summaryCards" :key="card.title">
            <v-card class="text-center pa-3" :color="card.color">
              <v-card-text>
                <div class="text-h4 font-weight-bold" :class="card.textColor">{{ card.value }}</div>
                <div class="text-subtitle-1">{{ card.title }}</div>
                <v-icon :color="card.iconColor" size="40" class="mt-2">{{ card.icon }}</v-icon>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Biểu đồ -->
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>{{ labels.packageChartTitle }}</v-card-title>
              <v-card-text class="chart-container">
                <highcharts v-if="chartCombinedData.length > 0" :options="packageChartOptions"></highcharts>
                <div v-else class="text-center pa-4">{{ labels.noChartData }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card>
              <v-card-title>{{ labels.weightChartTitle }}</v-card-title>
              <v-card-text class="chart-container">
                <highcharts v-if="chartCombinedData.length > 0" :options="weightChartOptions"></highcharts>
                <div v-else class="text-center pa-4">{{ labels.noChartData }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Bảng thống kê theo đại lý -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2">fa-table</v-icon>
                {{ labels.summaryByAgent }}
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="customerHeaders"
                  :items="summaryByCustomer"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="10"
                >
                  <template slot="item.total_weight" slot-scope="{ item }">
                    {{ formatWeight(item.total_weight) }}
                  </template>
                  <template slot="item.exported_weight" slot-scope="{ item }">
                    {{ formatWeight(item.exported_weight) }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Bảng thống kê chi tiết theo ngày -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon class="mr-2" color="primary">fa-table-large</v-icon>
                <span class="text-h6 font-weight-bold">{{ labels.dailyStatistics }}</span>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="dailyHeaders"
                  :items="dailyStats"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="15"
                  sort-by="report_date"
                  sort-desc
                >
                  <template slot="item.report_date" slot-scope="{ item }">
                    {{ formatDate(item.report_date) }}
                  </template>
                  <template slot="item.total_weight" slot-scope="{ item }">
                    {{ formatWeight(item.total_weight) }}
                  </template>
                  <template slot="item.exported_weight" slot-scope="{ item }">
                    {{ formatWeight(item.exported_weight) }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import configs from '@/configs';

export default {
  name: 'BigPackageStatistics',
  data() {
    return {
      fromDate: this.getDefaultFromDate(),
      toDate: this.getDefaultToDate(),
      fromDateMenu: false,
      toDateMenu: false,
      selectedCustomer: ''
    };
  },
  computed: {
    ...mapState({
      loading: state => state.bigPackageStatistics.loading,
      statisticsData: state => state.bigPackageStatistics.data,
      customers: state => state.customer.master.data
    }),
    isChinessLanguage() {
      return (this.$store.state.authentication.user.roletype & configs.ROLE_TYPE.StoreTQ) == configs.ROLE_TYPE.StoreTQ && this.$store.state.authentication.user.ischinesslanguage;
    },
    selectedCustomerID() {
      return this.$store.state.authentication.user.customerid;
    },
    labels() {
      return this.isChinessLanguage ? {
        // Tiêu đề chính
        reportTitle: '包裹统计报告',
        
        // Bộ lọc
        fromDate: '开始日期',
        toDate: '结束日期',
        agent: '代理',
        allAgents: '所有代理',
        update: '更新',
        
        // Tổng quan thống kê
        totalPackages: '总包裹数',
        totalWeight: '总重量 (kg)',
        exportedPackages: '已出库包裹',
        exportedWeight: '已出库重量 (kg)',
        
        // Biểu đồ
        packageChartTitle: '每日包裹数量图表',
        weightChartTitle: '每日包裹重量图表',
        noChartData: '无图表数据',
        
        // Bảng thống kê
        summaryByAgent: '按代理汇总统计',
        dailyStatistics: '按日期和代理统计包裹',
        
        // Headers bảng đại lý
        agent: '代理',
        totalPackagesHeader: '总包裹',
        totalWeightHeader: '总重量 (kg)',
        exportedPackagesHeader: '已出库',
        exportedWeightHeader: '已出库重量 (kg)',
        
        // Headers bảng hàng ngày
        date: '日期',
        packageCount: '包裹数',
        weight: '重量 (kg)',
        exportedCount: '出库数',
        exportedWeightHeader: '出库重量 (kg)',
        
        // Biểu đồ series
        newPackages: '新建包裹',
        exportedPackages: '出库包裹',
        newPackageWeight: '新建包裹重量',
        exportedPackageWeight: '出库包裹重量'
      } : {
        // Tiêu đề chính
        reportTitle: 'Báo cáo thống kê bao hàng',
        
        // Bộ lọc
        fromDate: 'Từ ngày',
        toDate: 'Đến ngày',
        agent: 'Đại lý',
        allAgents: 'Tất cả đại lý',
        update: 'Cập nhật',
        
        // Tổng quan thống kê
        totalPackages: 'Tổng số bao hàng',
        totalWeight: 'Tổng cân nặng (kg)',
        exportedPackages: 'Bao đã xuất kho',
        exportedWeight: 'Cân đã xuất (kg)',
        
        // Biểu đồ
        packageChartTitle: 'Biểu đồ số lượng bao hàng theo ngày',
        weightChartTitle: 'Biểu đồ cân nặng bao hàng theo ngày',
        noChartData: 'Không có dữ liệu biểu đồ',
        
        // Bảng thống kê
        summaryByAgent: 'Thống kê tổng hợp theo đại lý',
        dailyStatistics: 'Thống kê bao hàng theo ngày và đại lý',
        
        // Headers bảng đại lý
        agent: 'Đại lý',
        totalPackagesHeader: 'Tổng bao hàng',
        totalWeightHeader: 'Tổng cân nặng (kg)',
        exportedPackagesHeader: 'Bao đã xuất',
        exportedWeightHeader: 'Cân đã xuất (kg)',
        
        // Headers bảng hàng ngày
        date: 'Ngày',
        packageCount: 'Số bao',
        weight: 'Cân nặng (kg)',
        exportedCount: 'Số bao xuất',
        exportedWeightHeader: 'Cân nặng xuất (kg)',
        
        // Biểu đồ series
        newPackages: 'Bao hàng tạo mới',
        exportedPackages: 'Bao hàng xuất kho',
        newPackageWeight: 'Cân nặng bao tạo mới',
        exportedPackageWeight: 'Cân nặng bao xuất kho'
      };
    },
    customerHeaders() {
      return [
        { text: this.labels.agent, value: 'customer_name', sortable: true },
        { text: this.labels.totalPackagesHeader, value: 'total_packages', align: 'center', width: '120px' },
        { text: this.labels.totalWeightHeader, value: 'total_weight', align: 'center', width: '140px' },
        { text: this.labels.exportedPackagesHeader, value: 'exported_packages', align: 'center', width: '120px' },
        { text: this.labels.exportedWeightHeader, value: 'exported_weight', align: 'center', width: '140px' }
      ];
    },
    dailyHeaders() {
      return [
        { text: this.labels.date, value: 'report_date', sortable: true, width: '120px' },
        { text: this.labels.agent, value: 'customer_name', sortable: true },
        { text: this.labels.packageCount, value: 'total_packages', align: 'center', width: '100px' },
        { text: this.labels.weight, value: 'total_weight', align: 'center', width: '140px' },
        { text: this.labels.exportedCount, value: 'exported_packages', align: 'center', width: '120px' },
        { text: this.labels.exportedWeightHeader, value: 'exported_weight', align: 'center', width: '160px' }
      ];
    },
    dailyStats() {
      return this.statisticsData && this.statisticsData.daily_stats ? this.statisticsData.daily_stats : [];
    },
    summaryByCustomer() {
      return this.statisticsData && this.statisticsData.summary_by_customer ? this.statisticsData.summary_by_customer : [];
    },
    totalSummary() {
      return this.statisticsData && this.statisticsData.total_summary ? this.statisticsData.total_summary : {};
    },
    chartCombinedData() {
      return this.statisticsData && this.statisticsData.chart_combined_data ? this.statisticsData.chart_combined_data : [];
    },
    customerOptions() {
      const options = [{ text: this.labels.allAgents, value: '' }];
      if (this.customers) {
        options.push(...this.customers.map(customer => ({
          text: customer.Username,
          value: customer.ID
        })));
      }
      return options;
    },
    summaryCards() {
      const summary = this.totalSummary;
      return [
        {
          title: this.labels.totalPackages,
          value: this.formatNumber(summary.total_packages || 0),
          icon: 'fa-box',
          color: 'blue lighten-5',
          textColor: 'blue--text text--darken-2',
          iconColor: 'blue darken-1'
        },
        {
          title: this.labels.totalWeight,
          value: this.formatWeight(summary.total_weight || 0),
          icon: 'fa-weight',
          color: 'green lighten-5',
          textColor: 'green--text text--darken-2',
          iconColor: 'green darken-1'
        },
        {
          title: this.labels.exportedPackages,
          value: this.formatNumber(summary.exported_packages || 0),
          icon: 'fa-truck',
          color: 'orange lighten-5',
          textColor: 'orange--text text--darken-2',
          iconColor: 'orange darken-1'
        },
        {
          title: this.labels.exportedWeight,
          value: this.formatWeight(summary.exported_weight || 0),
          icon: 'fa-balance-scale',
          color: 'purple lighten-5',
          textColor: 'purple--text text--darken-2',
          iconColor: 'purple darken-1'
        }
      ];
    },
    packageChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 350,
          backgroundColor: 'transparent'
        },
        title: { text: null },
        xAxis: {
          categories: this.chartCombinedData.map(item => this.formatDate(item.report_date)),
          title: { text: this.labels.date }
        },
        yAxis: {
          title: { text: this.labels.packageCount }
        },
        series: [{
          name: this.labels.newPackages,
          data: this.chartCombinedData.map(item => parseInt(item.created_packages || 0)),
          color: '#2196F3'
        }, {
          name: this.labels.exportedPackages,
          data: this.chartCombinedData.map(item => parseInt(item.exported_packages || 0)),
          color: '#FF9800'
        }],
        credits: { enabled: false },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: false
            }
          }
        }
      };
    },
    weightChartOptions() {
      return {
        chart: {
          type: 'line',
          height: 350,
          backgroundColor: 'transparent'
        },
        title: { text: null },
        xAxis: {
          categories: this.chartCombinedData.map(item => this.formatDate(item.report_date)),
          title: { text: this.labels.date }
        },
        yAxis: {
          title: { text: this.labels.weight }
        },
        series: [{
          name: this.labels.newPackageWeight,
          data: this.chartCombinedData.map(item => parseFloat(item.created_weight || 0)),
          color: '#4CAF50'
        }, {
          name: this.labels.exportedPackageWeight,
          data: this.chartCombinedData.map(item => parseFloat(item.exported_weight || 0)),
          color: '#F44336'
        }],
        credits: { enabled: false },
        plotOptions: {
          line: {
            dataLabels: {
              enabled: false
            }
          }
        }
      };
    }
  },
  methods: {
    getDefaultFromDate() {
      const date = new Date();
      date.setDate(date.getDate() - 30); // 30 ngày trước
      return date.toISOString().substr(0, 10);
    },
    getDefaultToDate() {
      return new Date().toISOString().substr(0, 10);
    },
    loadStatistics() {
      const params = {
        from_date: this.fromDate,
        to_date: this.toDate,
        customer_id: this.selectedCustomer
      };
      if( this.selectedCustomerID > 0 ) {
        params.customer_id = this.selectedCustomerID;
      }
      this.$store.dispatch('bigPackageStatistics/getStatistics', params);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('vi-VN');
    },
    formatWeight(weight) {
      if (!weight) return '0.00';
      return parseFloat(weight).toLocaleString('vi-VN', { 
        minimumFractionDigits: 2,
        maximumFractionDigits: 2 
      });
    },
    formatNumber(num) {
      if (!num) return '0';
      return parseInt(num).toLocaleString('vi-VN');
    },
    calculateExportRate(exported, total) {
      if (!total || total === 0) return 0;
      return Math.round((exported / total) * 100);
    },
    getExportRateColor(exported, total) {
      const rate = this.calculateExportRate(exported, total);
      if (rate >= 80) return 'green';
      if (rate >= 50) return 'orange';
      return 'red';
    },
    getCustomerList() {
      this.$store.dispatch("customer/getCustomerList", {
        is_master: 1,
        fields: ["ID", "Username", "CustomerCode"]
      });
    }
  },
  mounted() {
    this.getCustomerList();
    this.loadStatistics();
  }
};
</script>

<style scoped>
.dashboard-card {
  border-radius: 12px;
}

.chart-container {
  min-height: 350px;
}

.statistics-summary .v-card {
  transition: transform 0.2s ease-in-out;
}

.statistics-summary .v-card:hover {
  transform: translateY(-2px);
}

.text-h4 {
  font-size: 1.8rem !important;
}

.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}
</style>